import { TFunction } from 'i18next';
import { FileVolumeIcon, WorkflowIcon } from 'lucide-react';
import { Trans } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useMarkdownToHtml } from '@/hooks/useMarkdownToHtml';
import { cn } from '@/lib/utils';
import { Attachment } from '@/modules/humanChat/components/Conversation/MessageArea/Attachment/Attachment';
import Attachments from '@/modules/humanChat/components/Conversation/MessageArea/Attachment/Attachments';
import { selectSessionSource } from '@/modules/tryIt/redux/selectors';
import { Attachment as AttachmentType } from '@/shared/types/chat';
import { Entity, MessageReminder } from '@/shared/types/tryIt';
import { convertMinutesToSeconds, formatSecondsToReadable } from '@/util/dateUtils';
import { escapeStandaloneOrderedListMarkers } from '@/util/markdown';
import { splitArray } from '@/util/util';

import { highlightEntities } from './helper';

interface PromptMessageProps {
  text: string;
  entities: Entity[];
  onGoToEntity: (entity: string) => void;
  entityHref?: (entity: string) => string;
  t: TFunction;
  isAudio: boolean;
  attachments: AttachmentType[];
  reminder?: MessageReminder;
  isFlowResponse?: boolean;
  flat?: boolean;
  turnRow?: boolean;
}

export const PromptMessage = ({
  text,
  entities,
  onGoToEntity,
  entityHref,
  t,
  isAudio,
  attachments,
  reminder,
  isFlowResponse,
  flat = false,
  turnRow = false,
}: PromptMessageProps) => {
  const source = useSelector(selectSessionSource);
  const { html } = useMarkdownToHtml(escapeStandaloneOrderedListMarkers(text));
  const hasEntities = entities && entities.length > 0;

  // Reminder banner
  if (!text && reminder) {
    if (turnRow) {
      return null;
    }

    // Backward compatibility: some reminders may still have 'minutes' field from legacy data
    const legacyReminder = reminder as MessageReminder & { reminder_seconds?: number };
    const seconds =
      legacyReminder.reminder_seconds ?? convertMinutesToSeconds(reminder.minutes ?? 0);

    return (
      <p className="mb-6 rounded-sm bg-muted p-2 text-center text-sm leading-5 font-normal text-muted-foreground">
        <Trans
          i18nKey={'conversation.after_duration_of_inactivity'}
          values={{ 0: formatSecondsToReadable(seconds) }}
        />
      </p>
    );
  }

  // Flow completion indicator (user completed a WhatsApp Flow)
  if (isFlowResponse) {
    return (
      <p className="mb-6 flex items-center gap-2 rounded-sm bg-muted p-2 text-sm leading-5 font-normal text-muted-foreground">
        <WorkflowIcon className="size-4" />
        {t('conversation.flow_completed')}
      </p>
    );
  }

  const bubble = (
    <div
      className={cn(
        'ml-auto flex w-fit max-w-md flex-col text-sm leading-5 font-normal [overflow-wrap:anywhere] break-words [word-break:break-word] text-primary-foreground shadow-[0_30px_30px_0_rgba(0,0,0,0.02),0_0_8px_0_rgba(0,0,0,0.03),0_1px_0_0_rgba(0,0,0,0.05)]',
        flat ? 'gap-1 rounded-lg bg-primary' : 'rounded-md bg-primary',
        !turnRow && 'min-w-20 px-4 py-3',
        turnRow && 'items-start rounded-br-sm px-3 py-2.5'
      )}
    >
      {attachments && attachments.length > 0 && (
        <div className={cn('flex w-full justify-end', !flat && text && 'mb-2')}>
          {splitArray(attachments, 4).map((chunk, chunkIndex) => (
            <Attachments
              // eslint-disable-next-line react/no-array-index-key
              key={chunkIndex}
            >
              {chunk.map((attachment) => (
                <Attachment
                  key={attachment?.id ?? attachment.url}
                  attachment={attachment}
                  source={source}
                  isSingleItem={attachments.length === 1}
                  bg="dark"
                  authorType="visitor"
                />
              ))}
            </Attachments>
          ))}
        </div>
      )}
      {isAudio && !turnRow && (
        <span className="flex items-center gap-1">
          <FileVolumeIcon size={16} />
          <span className="py-1">{t('conversation.audio_message')}</span>
        </span>
      )}

      {hasEntities ? (
        <span>{highlightEntities(text, entities, onGoToEntity, entityHref)}</span>
      ) : (
        <span className="[&_a]:underline" dangerouslySetInnerHTML={{ __html: html }} />
      )}
    </div>
  );

  if (!isAudio || !turnRow) {
    return bubble;
  }

  return (
    <div className="ml-auto flex w-fit max-w-md flex-col items-end gap-1.5">
      {bubble}
      <span className="flex items-center gap-1 text-xs leading-4 font-normal text-muted-foreground">
        <FileVolumeIcon size={16} />
        {t('conversation.audio_message')}
      </span>
    </div>
  );
};
